import {Box, Typography, Card} from '@mui/material';
import {Image} from '@jobseeker/lotus/components/Image';
import React, {useEffect, useRef, useState} from 'react';
import moment from 'moment';
import {convertDate} from '@jobseeker/common-core/utils';
import {useSelector} from 'react-redux';
import {AppState} from '@jobseeker/common-core/types';
import {useIsLargeDevice, useWrappedTheme} from '@jobseeker/lotus/hooks';
import {ButtonV2, VersionVariants} from '@jobseeker/lotus/elements';
import {CheckCircle} from '@jobseeker/lotus/assets/icons';
import {ProgessApiResponse} from './types';
import competitionService from '@jobseeker/common-core/services/competition.service';
import {Alert} from '@jobseeker/lotus/components';

const Timeline: React.FC = () => {
  const {userId} = useSelector((state: AppState) => state.auth);
  const isLoggedIn = useSelector((state: AppState) => state.auth.isLoggedIn);
  const isAboveMobile = useIsLargeDevice('sm');
  const theme = useWrappedTheme();
  const currentDate = moment();
  const timelineRef = useRef<HTMLDivElement | null>(null);
  const messageRef = useRef<HTMLDivElement | null>(null);
  const [barHeight, setBarHeight] = useState<number>(0);
  const [progressInfo, setProgressInfo] = useState<ProgessApiResponse | null>(
    null
  );
  const {
    timelines: {
      registration,
      round1,
      round2,
      config: {
        competitionName,
        competitionId,
        roundsContent: {
          label,
          registration: RegistrationRound,
          round1: Round1,
          round2: Round2,
        },
        evaluationCriteria,
      },
    },
  } = useSelector((state: AppState) => state.competition);

  const {competitionIdentity, testSlug} = useSelector(
    (state: AppState) => state.user?.profile
  );
  const registeredForCompetition = competitionIdentity && testSlug;
  const [messageWidth, setMessageWidth] = useState('auto');

  useEffect(() => {
    if (messageRef.current) {
      setMessageWidth(messageRef.current.scrollWidth + 'px');
    }
  }, [messageRef]);
  useEffect(() => {
    const getProgressInfo = async () => {
      const progressInfoData = await competitionService.fetchProgressInfo(
        competitionId
      );
      setProgressInfo(progressInfoData);
    };
    if (userId !== '-1' && isLoggedIn) {
      getProgressInfo();
    }
  }, [userId, isLoggedIn]);

  useEffect(() => {
    const updateBarHeight = () => {
      if (timelineRef.current) {
        const firstChild = timelineRef.current.firstElementChild;
        const lastChild = timelineRef.current.lastElementChild;

        if (firstChild && lastChild) {
          const firstChildRect = firstChild.getBoundingClientRect();
          const lastChildRect = lastChild.getBoundingClientRect();
          const firstChildTop = firstChildRect.top;
          const lastChildTop = lastChildRect.top;
          setBarHeight(lastChildTop - firstChildTop);
        }
      }
    };
    updateBarHeight();
    const debounceUpdate = setTimeout(updateBarHeight, 100);
    window.addEventListener('resize', updateBarHeight);
    return () => {
      clearTimeout(debounceUpdate);
      window.removeEventListener('resize', updateBarHeight);
    };
  }, [timelineRef, registration]);

  const roundsProgess = progressInfo?.rounds || [];
  const round1Progress = roundsProgess[1] || {};
  const round2Progress = roundsProgess[2] || {};
  // const round3Progress = roundsProgess[3] || {};

  const events = [
    {
      round: 0,
      title: RegistrationRound?.title,
      description: RegistrationRound?.description,
      subDesc: RegistrationRound?.subDesc,
      startDate: convertDate(
        registration?.startDate,
        'YYYY-MM-DD HH:mm:ss',
        'Do MMM YY'
      ),
      endDate: convertDate(
        registration?.endDate,
        'YYYY-MM-DD HH:mm:ss',
        'Do MMM YY'
      ),
      buttonLink: '',
      buttonText: '',
      message: '',
      activeRound: false,
    },
    {
      round: 1,
      title: Round1?.title,
      description: Round1?.description,
      subDesc: Round1?.subDesc,
      startDate: convertDate(
        round1?.startDate,
        'YYYY-MM-DD HH:mm:ss',
        'Do MMM YY'
      ),
      endDate: convertDate(round1?.endDate, 'YYYY-MM-DD HH:mm:ss', 'Do MMM YY'),
      buttonLink:
        moment(round1?.startDate) <= currentDate &&
        moment(round1?.endDate) >= currentDate &&
        round1Progress.status === 0
          ? `/${competitionName}/round-one`
          : moment(round1?.resultDeclarationDate) <= currentDate
          ? `/${competitionName}/round-one/result`
          : 'javascript:void();',
      notStarted: moment(round1?.startDate) > currentDate,
      buttonText:
        moment(round1?.startDate) > currentDate
          ? 'Not Started Yet'
          : moment(round1?.startDate) <= currentDate &&
            moment(round1?.endDate) >= currentDate &&
            round1Progress.status === 0
          ? 'Start Quiz'
          : (moment(round1?.endDate) < currentDate ||
              (moment(round1?.endDate) > currentDate &&
                round1Progress.status === 1)) &&
            moment(round1?.resultDeclarationDate) > currentDate
          ? 'Waiting for result'
          : moment(round1?.resultDeclarationDate) <= currentDate
          ? 'Check Result'
          : '',
      message:
        progressInfo && round1Progress.action === 1
          ? moment(round1?.resultDeclarationDate) <= currentDate &&
            round1Progress.shortListMessage
          : round1Progress.action === 2
          ? moment(round1?.resultDeclarationDate) <= currentDate &&
            round1Progress.rejectedMessage
          : moment(round1?.resultDeclarationDate) >= currentDate &&
            round1Progress.status === 1
          ? round1Progress.submitMessage
          : '',
      rejected: round1Progress.action === 2,
      activeRound: progressInfo && progressInfo?.currentRound === 1,
    },
    {
      round: 2,
      title: Round2?.title,
      description: Round2?.description,
      subDesc: Round2?.subDesc,
      startDate: convertDate(
        round2?.startDate,
        'YYYY-MM-DD HH:mm:ss',
        'Do MMM YY'
      ),
      endDate: convertDate(round2?.endDate, 'YYYY-MM-DD HH:mm:ss', 'Do MMM YY'),
      buttonLink:
        moment(round2?.startDate) <= currentDate &&
        moment(round2?.endDate) >= currentDate &&
        round2Progress.status === 0
          ? `/${competitionName}/round-two`
          : moment(round2?.resultDeclarationDate) <= currentDate
          ? `/${competitionName}/winners`
          : (moment(round2?.endDate) < currentDate ||
              (moment(round2?.endDate) > currentDate &&
                round2Progress.status === 1)) &&
            moment(round2?.resultDeclarationDate) > currentDate
          ? `/${competitionName}/round-two`
          : 'javascript:void()',
      notStarted: moment(round2?.startDate) > currentDate,
      buttonText:
        moment(round2?.startDate) > currentDate
          ? 'Not Started Yet'
          : moment(round2?.startDate) <= currentDate &&
            moment(round2?.endDate) >= currentDate &&
            round2Progress.status === 0
          ? 'Start'
          : (moment(round2?.endDate) < currentDate ||
              (moment(round2?.endDate) > currentDate &&
                round2Progress.status === 1)) &&
            moment(round2?.resultDeclarationDate) > currentDate
          ? 'View submission'
          : moment(round2?.resultDeclarationDate) <= currentDate
          ? 'Check Result'
          : '',
      message:
        progressInfo &&
        round2Progress?.action === 1 &&
        moment(round2?.resultDeclarationDate) <= currentDate
          ? round2Progress?.shortListMessage
          : moment(round2?.resultDeclarationDate) <= currentDate &&
            round2Progress?.action === 2
          ? round2Progress?.rejectedMessage
          : moment(round2?.resultDeclarationDate) >= currentDate &&
            round2Progress?.status == 1
          ? round2Progress?.submitMessage
          : '',
      rejected: round2Progress.action === 2,
      activeRound: progressInfo && progressInfo?.currentRound === 2,
    },
    // {
    //   round: 3,
    //   title: 'Round 3- The Elevator Pitch',
    //   description: `The Selected students will be presented with case problems from the companies they have applied to. Solution Pitches have to be shared within the stipulated timelines by the firms. PPIs/PPOs will be shared basis your pitch Please share a PPT in not more than 12 Slides , along with a 3 min video explaining your solution`,
    //   subDesc: `Please share a PPT in not more than 12 Slides , along with a 3 min video explaining your solution`,
    //   startDate: convertDate(
    //     round3?.startDate,
    //     'YYYY-MM-DD HH:mm:ss',
    //     'Do MMM YY'
    //   ),
    //   endDate: convertDate(round3?.endDate, 'YYYY-MM-DD HH:mm:ss', 'Do MMM YY'),
    //   buttonLink:
    //     moment(round3?.startDate) <= currentDate &&
    //     moment(round3?.endDate) >= currentDate &&
    //     round3Progress.status === 0
    //       ? `/epoch/round-three`
    //       : (moment(round3?.endDate) < currentDate ||
    //           (moment(round3?.endDate) > currentDate &&
    //             round3Progress.status === 1)) &&
    //         moment(round2?.resultDeclarationDate) > currentDate
    //       ? '/epoch/results/round-three'
    //       : 'javascript:void()',
    //   notStarted: moment(round3?.startDate) > currentDate,
    //   buttonText:
    //     moment(round3?.startDate) > currentDate
    //       ? 'Not Started Yet'
    //       : moment(round3?.startDate) <= currentDate &&
    //         moment(round3?.endDate) >= currentDate &&
    //         round3Progress.status === 0
    //       ? 'Start'
    //       : (moment(round3?.endDate) < currentDate ||
    //           (moment(round3?.endDate) > currentDate &&
    //             round3Progress.status === 1)) &&
    //         moment(round3?.resultDeclarationDate) > currentDate
    //       ? 'View submission'
    //       : moment(round3?.resultDeclarationDate) < currentDate
    //       ? 'Check Result'
    //       : '',
    //   message:
    //     progressInfo &&
    //     round3Progress?.action === 1 &&
    //     moment(round3?.resultDeclarationDate) <= currentDate
    //       ? round3Progress?.shortListMessage
    //       : moment(round3?.resultDeclarationDate) <= currentDate &&
    //         round3Progress?.action === 2
    //       ? round3Progress?.rejectedMessage
    //       : moment(round3?.resultDeclarationDate) >= currentDate &&
    //         round3Progress?.status == 1
    //       ? round3Progress?.submitMessage
    //       : '',
    //   rejected: round3Progress.action === 2,
    //   activeRound: progressInfo && progressInfo?.currentRound === 3,
    // },
  ];
  // events.pop();
  if (registeredForCompetition) {
    events.shift();
  }

  if (!registration.endDate) {
    return false;
  }
  return (
    <Box id="rounds-timelines" sx={{marginBottom: 4}}>
      <Card
        sx={{
          width: '100%',
          margin: '0 auto',
          borderRadius: 3,
        }}
      >
        <Box id="timeline">
          <Box
            sx={{
              width: {xs: '80%', md: '100%'},
              margin: {xs: '0 auto', md: 0},
              display: 'flex',
              flexDirection: 'column',
              textAlign: 'center',
              marginTop: {xs: 2, md: 5},
              justifyContent: 'center',
            }}
          >
            <Typography
              sx={{
                fontSize: {
                  xs: theme.typography.pxToRem(22),
                  md: theme.typography.pxToRem(42),
                },
                fontWeight: 600,
                lineHeight: 1,
                textAlign: 'center',
              }}
            >
              Rounds & Timelines{' '}
              {label && (
                <Box
                  component="span"
                  sx={{
                    display: {xs: 'none', md: 'inline-block'},
                    fontSize: theme.typography.pxToRem(22),
                    fontWeight: 300,
                    lineHeight: 1,
                  }}
                >
                  ({label})
                </Box>
              )}
            </Typography>
          </Box>

          <Box
            id="timeline-content"
            sx={{
              marginLeft: 7.5,
              marginTop: 6.75,
              paddingLeft: {xs: 0, md: 4.375},
              paddingBottom: 3.75,
              paddingRight: {xs: 2, md: 6},
            }}
          >
            <Box
              sx={{
                width: 4,
                background: '#c3efe6',
                height: barHeight || '762px',
                position: 'absolute',
                marginLeft: -5,
              }}
            ></Box>
            <Box ref={timelineRef}>
              {events.map((item, index) => {
                const isLastItem = index === events.length - 1;
                return (
                  <Box key={index} className={'timeline-box'}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        borderBottom:
                          index < events.length - 1 ? '2px solid #ededf4' : '',
                        marginBottom: !isLastItem ? 3.5 : 0,
                        position: 'relative',
                      }}
                    >
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          backgroundColor: '#c3efe6',
                          position: 'absolute',
                          left: -45,
                          top: 0,
                        }}
                      />
                      {registeredForCompetition &&
                        progressInfo &&
                        progressInfo?.currentRound >= index + 1 && (
                          <Image
                            src={
                              item.activeRound
                                ? 'https://static.iimjobs.com/epoch/static/assets/emptyGreenCircle.svg'
                                : CheckCircle
                            }
                            alt="Tick Icon"
                            style={{
                              width: '22px',
                              height: '22px',
                              position: 'absolute',
                              top: 0,
                              left: -48,
                            }}
                          />
                        )}
                      <Box sx={{marginTop: '-5px'}}>
                        <Typography
                          sx={{
                            fontSize: {xs: '17px', md: '20px'},
                            fontWeight: 500,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 300,
                            fontSize: {xs: '16px', md: '18px'},
                            lineHeight: 1.61,
                          }}
                        >
                          {item.description}
                          {item.subDesc.length > 0 && (
                            <>
                              <br />
                              <br />
                              {/* {item.round === 0 && <br />} */}
                              {/* {item.round === 2 ? (
                                item.subDesc
                              ) : (
                                <b>{item.subDesc}</b>
                              )} */}
                              <Typography
                                sx={{
                                  fontWeight: item.round === 2 ? 300 : 500,
                                  fontSize: {
                                    xs: '16px',
                                    md: '18px',
                                    lineHeight: 1.61,
                                  },
                                }}
                              >
                                {item.subDesc}
                              </Typography>
                            </>
                          )}
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            width: '100%',
                            marginBottom: 3.5,
                            alignItems: {xs: 'flex-start', md: 'center'},
                            gap: {xs: 0, md: 10},
                            flexDirection: {xs: 'column', md: 'row'},
                          }}
                        >
                          <Box sx={{display: 'flex', marginTop: 3}}>
                            <Image
                              src={
                                'https://static.iimjobs.com/epoch/static/assets/calendar.svg'
                              }
                              alt="calendar"
                              height={24}
                              width={24}
                            />
                            <Typography variant="body1">
                              <Box component="span" sx={{marginLeft: '8px'}}>
                                Start Date
                              </Box>
                              <br />
                              <Box
                                component="span"
                                sx={{
                                  fontWeight: 600,
                                  color: 'primary.main',
                                  marginLeft: '8px',
                                }}
                              >
                                {item.startDate}
                              </Box>
                            </Typography>
                          </Box>
                          <Box sx={{display: 'flex', marginTop: 3}}>
                            <Image
                              src={
                                'https://static.iimjobs.com/epoch/static/assets/calendar.svg'
                              }
                              alt="calendar"
                              height={24}
                              width={24}
                            />
                            <Typography variant="body1">
                              <Box component="span" sx={{marginLeft: '8px'}}>
                                End Date
                              </Box>
                              <br />
                              <Box
                                component="span"
                                sx={{
                                  fontWeight: 600,
                                  color: 'primary.main',
                                  marginLeft: 1,
                                }}
                              >
                                {item.endDate}
                              </Box>
                            </Typography>
                          </Box>
                        </Box>
                        {(registeredForCompetition ||
                          (isLastItem &&
                            moment(round2.resultDeclarationDate) <=
                              currentDate)) &&
                          item.buttonText.length > 0 && (
                            <Box sx={{marginBottom: 4}}>
                              <ButtonV2
                                type="submit"
                                sx={{
                                  // width: 110,
                                  height: 50,
                                  padding: 3,
                                  borderRadius: 9,
                                  background:
                                    item.buttonText === 'Waiting for result'
                                      ? '#e7f8f4'
                                      : 'primary.main',
                                  ':hover': {
                                    background:
                                      item.buttonText === 'Waiting for result'
                                        ? '#e7f8f4'
                                        : '',
                                  },
                                  mt: 1,
                                  opacity: item.notStarted ? 0.3 : 'unset',
                                  color:
                                    item.buttonText === 'Waiting for result'
                                      ? '#50ac98'
                                      : '#fff',
                                }}
                                href={item.buttonLink}
                                target={
                                  item.buttonText === 'Waiting for result'
                                    ? '_self'
                                    : '_blank'
                                }
                                variant="contained"
                                size="large"
                              >
                                {item.buttonText}
                              </ButtonV2>
                            </Box>
                          )}
                        {registeredForCompetition &&
                          item.message &&
                          item.message.length > 0 && (
                            <Box
                              ref={messageRef}
                              sx={{
                                display: 'inline-block',
                                whiteSpace: 'nowrap',
                                width: messageWidth,
                              }}
                            >
                              <Alert
                                variant={VersionVariants.VERSION_TWO}
                                show={true}
                                sx={{
                                  marginBottom: 5,
                                  textAlign: 'center',
                                  background: item.rejected ? '#fff0f0' : '',
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: '14px',
                                    textWrap: 'wrap',
                                    color: item.rejected
                                      ? '#d11124'
                                      : 'primary.main',
                                  }}
                                  dangerouslySetInnerHTML={{
                                    __html: item.message,
                                  }}
                                />
                              </Alert>
                            </Box>
                          )}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
            {/* <Box
              sx={{
                fontSize: '13px',
                fontWeight: 400,
                padding: '10px',
                borderRadius: '5px',
                border: 'solid 1px #f3f3f3',
                backgroundColor: '#f9f9f9',
                color: '#4B4B4B',
                marginLeft: {xs: -5, md: 0},
                marginRight: {xs: 0, md: '47px'},
              }}
            >
              <b>Note:</b> The content for all three rounds (quiz, case and surprise rounds task) will vary based on the selected company which will be selected at the time of registration.
            </Box> */}
            <Box
              sx={{
                borderTop: '2px solid #ededf4',
                marginTop: 5,
                marginLeft: {xs: -9.5, md: -11.875},
                marginRight: -6,
              }}
            ></Box>
          </Box>

          <Box
            sx={{
              // borderTop: '2px solid #ededf4',
              marginBottom: 5.5,
              marginLeft: {xs: 4, md: 7.5},
            }}
          >
            <Typography
              sx={{
                fontSize: {xs: '16px', md: '18px'},
                fontWeight: 500,
                marginTop: 2,
              }}
            >
              Evaluation Criteria&apos;s:
            </Typography>
            <Typography
              sx={{fontSize: {xs: '16px', md: '18px'}, fontWeight: 300}}
              paragraph
            >
              Your case solution will be evaluated on the following parameters:
            </Typography>
            <Box>
              <ul
                style={{
                  listStyle:
                    'url("https://static.iimjobs.com/epoch/static/assets/tick.svg") inside',
                  margin: 0,
                  paddingInlineStart: 0,
                }}
              >
                {evaluationCriteria?.map((item, index) => (
                  <li
                    key={index}
                    style={{
                      fontSize: isAboveMobile ? '16px' : '14px',
                      fontWeight: 500,
                      lineHeight: isAboveMobile ? '3.13' : '2.5',
                    }}
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </Box>
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

export default Timeline;
